import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { type Property, calculatePricePerSqft } from '../mocks/properties';

const PROPERTY_STORAGE_KEY = 'safaqat24.customProperties.v1';

export interface PropertyDraft {
  id?: string;
  title: string;
  price: number;
  bedrooms: number;
  bathrooms: number;
  sqft: number;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  latitude: number;
  longitude: number;
  imageUrl: string;
  galleryImages?: string[];
  description: string;
  propertyType: Property['propertyType'];
  yearBuilt: number;
  parking: boolean;
  petFriendly: boolean;
  furnished: boolean;
  utilities: string[];
  amenities: string[];
  agentName: string;
  agentPhone: string;
  agentEmail: string;
  status?: Property['status'];
  featured?: boolean;
}

interface PropertyContextValue {
  properties: Property[];
  customProperties: Property[];
  addProperty: (draft: PropertyDraft) => Property;
  getPropertyById: (id: string) => Property | undefined;
  getAvailableProperties: () => Property[];
  getFeaturedProperties: (limit?: number) => Property[];
}

const PropertyContext = createContext<PropertyContextValue | undefined>(undefined);

function isProperty(value: unknown): value is Property {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  const stringFields: Array<keyof Property> = [
    'id',
    'title',
    'address',
    'city',
    'state',
    'zipCode',
    'imageUrl',
    'description',
    'propertyType',
    'agentName',
    'agentPhone',
    'agentEmail',
    'dateAdded',
    'status',
    'lastUpdated'
  ];

  for (const key of stringFields) {
    if (typeof candidate[key] !== 'string') {
      return false;
    }
  }

  const numberFields: Array<keyof Property> = [
    'price',
    'bedrooms',
    'bathrooms',
    'sqft',
    'latitude',
    'longitude',
    'yearBuilt',
    'pricePerSqft'
  ];

  for (const key of numberFields) {
    if (typeof candidate[key] !== 'number') {
      return false;
    }
  }

  const booleanFields: Array<keyof Property> = ['parking', 'petFriendly', 'furnished', 'featured'];

  for (const key of booleanFields) {
    if (typeof candidate[key] !== 'boolean') {
      return false;
    }
  }

  if (!Array.isArray(candidate.utilities) || !Array.isArray(candidate.amenities)) {
    return false;
  }

  if (candidate.galleryImages && !Array.isArray(candidate.galleryImages)) {
    return false;
  }

  return true;
}

function readStoredProperties(): Property[] {
  if (typeof window === 'undefined') {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(PROPERTY_STORAGE_KEY);
    if (!raw) {
      return [];
    }

    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed.filter(isProperty);
  } catch (error) {
    console.warn('Failed to parse stored custom properties', error);
    return [];
  }
}

function generatePropertyId(): string {
  const unique = Math.random().toString(36).slice(2, 8);
  return `custom-${Date.now().toString(36)}-${unique}`;
}

function mergeProperties(customProperties: Property[]): Property[] {
  // Return only the user's real (custom) properties. The seeded mockProperties
  // used to be merged in as demo content, but that caused phantom listings to
  // appear on the dashboard for real accounts. Real listings now come from the
  // backend via the create flow (which writes to localStorage on success) and,
  // in a follow-up, directly from /api/properties?mine=true.
  return customProperties;
}

export function PropertyProvider({ children }: { children: ReactNode }) {
  const [customProperties, setCustomProperties] = useState<Property[]>(() => readStoredProperties());

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(PROPERTY_STORAGE_KEY, JSON.stringify(customProperties));
    } catch (error) {
      console.warn('Unable to persist custom properties to storage', error);
    }
  }, [customProperties]);

  const mergedProperties = useMemo(() => mergeProperties(customProperties), [customProperties]);

  const addProperty = useCallback((draft: PropertyDraft): Property => {
    const nowIso = new Date().toISOString();
    const sanitizedSqft = Number.isFinite(draft.sqft) && draft.sqft > 0 ? draft.sqft : 0;
    const property: Property = {
      ...draft,
      id: draft.id && draft.id.trim().length > 0 ? draft.id.trim() : generatePropertyId(),
      status: draft.status ?? 'available',
      featured: draft.featured ?? false,
      pricePerSqft: sanitizedSqft > 0 ? calculatePricePerSqft(draft.price, sanitizedSqft) : 0,
      dateAdded: nowIso,
      lastUpdated: nowIso
    };

    setCustomProperties((previous) => [...previous, property]);

    return property;
  }, []);

  const getPropertyById = useCallback(
    (id: string) => mergedProperties.find((property) => property.id === id),
    [mergedProperties]
  );

  const getAvailableProperties = useCallback(
    () => mergedProperties.filter((property) => property.status === 'available'),
    [mergedProperties]
  );

  const getFeaturedProperties = useCallback(
    (limit?: number) => {
      const featured = mergedProperties.filter((property) => property.featured && property.status === 'available');
      return typeof limit === 'number' ? featured.slice(0, limit) : featured;
    },
    [mergedProperties]
  );

  const value: PropertyContextValue = {
    properties: mergedProperties,
    customProperties,
    addProperty,
    getPropertyById,
    getAvailableProperties,
    getFeaturedProperties
  };

  return <PropertyContext.Provider value={value}>{children}</PropertyContext.Provider>;
}

export function usePropertyStore(): PropertyContextValue {
  const context = useContext(PropertyContext);

  if (!context) {
    throw new Error('usePropertyStore must be used inside a PropertyProvider');
  }

  return context;
}
